import { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import type { SharePermission, ResourceType } from "../services/sharing.service";
import { useSharing } from "../hooks/useSharing";
import { LIMITS, ERROR_MESSAGES } from "../lib/constants";
import { toast } from "../lib/toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { X, UserPlus, Mail, Users, Clock, CheckCircle2 } from "lucide-react";

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  resourceType: ResourceType;
  resourceId: number;
  resourceName: string;
}

export function ShareModal({
  open,
  onClose,
  resourceType,
  resourceId,
  resourceName,
}: ShareModalProps) {
  const { t } = useTranslation('common');
  const [inputValue, setInputValue] = useState("");
  const [emailTags, setEmailTags] = useState<string[]>([]);
  const [permission, setPermission] = useState<SharePermission>("view");
  const [inviting, setInviting] = useState(false);

  const permissionLabels: Record<SharePermission, string> = {
    view: t('share.permission_view'),
    edit: t('share.permission_edit'),
    owner: t('share.permission_owner'),
  };

  const { shares, loading, reload, invite, updatePermission, removeShare, resendInvite } =
    useSharing({ resourceType, resourceId, autoLoad: false });

  useEffect(() => {
    if (open) {
      reload();
    }
  }, [open, reload]);

  const acceptedShares = useMemo(
    () =>
      shares.filter(
        (share) =>
          share.status === "accepted" &&
          share.permission !== "owner" &&
          (share.origin ?? "share") === "share"
      ),
    [shares]
  );

  const pendingShares = useMemo(
    () =>
      shares.filter(
        (share) => share.status !== "accepted" && (share.origin ?? "share") === "share"
      ),
    [shares]
  );

  const getShareKey = (share: typeof shares[number]) =>
    share.id ?? `${share.origin ?? "share"}-${share.user_email}`;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      const email = inputValue.trim().toLowerCase();
      if (emailTags.length >= LIMITS.SHARE_EMAILS_MAX) {
        toast.warning(ERROR_MESSAGES.MAX_SHARE_EMAILS_REACHED);
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error(ERROR_MESSAGES.INVALID_EMAIL);
        return;
      }
      if (email && !emailTags.includes(email)) {
        setEmailTags([...emailTags, email]);
      }
      setInputValue("");
    }
  };

  const removeEmailTag = (emailToRemove: string) => {
    setEmailTags(emailTags.filter((email) => email !== emailToRemove));
  };

  const handleInvite = async () => {
    if (emailTags.length === 0) {
      return;
    }

    setInviting(true);
    try {
      await invite(emailTags, permission);
      setEmailTags([]);
      await reload();
    } finally {
      setInviting(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-lg">{resourceType === "project" ? t('share.share_project') : t('share.share_meeting')}</DialogTitle>
          <DialogDescription className="text-sm truncate">
            {resourceName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {}
          <Card className="shadow-none border">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                {t('share.invite_collaborators')}
              </CardTitle>
              <CardDescription className="text-xs">
                {resourceType === "project" ? t('share.add_emails_project') : t('share.add_emails_meeting')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 px-4 pb-4 pt-0">
              {}
              <div className="space-y-1.5">
                <div className="flex flex-wrap gap-1.5 p-2 border-border border rounded-lg bg-background min-h-[40px] focus-within:ring-2 focus-within:ring-ring transition-all">
                  {emailTags.map((email) => (
                    <Badge
                      key={email}
                      variant="default"
                      className="pl-2 pr-1 py-1 gap-1 h-6 text-xs bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-colors"
                    >
                      <Mail className="h-3 w-3" />
                      {email}
                      <button
                        onClick={() => removeEmailTag(email)}
                        className="ml-0.5 hover:bg-primary/30 rounded-full p-0.5 transition-colors"
                        aria-label={t('remove') + ' ' + email}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  <input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={emailTags.length === 0 ? t('share.email_placeholder') : ""}
                    className="flex-1 min-w-[150px] outline-none bg-transparent text-sm placeholder:text-muted-foreground"
                  />
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {t('share.email_hint')}
                </p>
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">{t('share.default_permission')}</span>
                  <Select value={permission} onValueChange={(value) => setPermission(value as SharePermission)}>
                    <SelectTrigger className="w-[110px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="view">{t('share.permission_view')}</SelectItem>
                      <SelectItem value="edit">{t('share.permission_edit')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleInvite}
                  disabled={inviting || emailTags.length === 0}
                  className="bg-primary hover:bg-primary/90"
                  size="sm"
                >
                  <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                  {inviting ? t('sending') : t('share.send_invites')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {}
          <Card className="shadow-none border">
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  {t('share.collaborators')}
                </CardTitle>
                {loading && (
                  <Badge variant="outline" className="animate-pulse text-xs">
                    <Clock className="h-3 w-3 mr-1" />
                    {t('loading')}
                  </Badge>
                )}
              </div>
              <CardDescription className="text-xs">
                {t('share.manage_permissions')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 px-4 pb-4 pt-0">
              {acceptedShares.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    {t('share.active')} ({acceptedShares.length})
                  </div>
                  <div className="space-y-1.5">
                    {acceptedShares.map((share) => (
                      <div key={getShareKey(share)} className="flex items-center justify-between gap-2 p-2 rounded-lg border bg-background">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Mail className="h-3.5 w-3.5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium truncate">
                              {share.user_email}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <Select
                            value={share.permission}
                            onValueChange={(value) =>
                              updatePermission(share.id ?? -1, value as SharePermission)
                            }
                          >
                            <SelectTrigger className="w-[90px] h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="view">{t('share.permission_view')}</SelectItem>
                              <SelectItem value="edit">{t('share.permission_edit')}</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            onClick={() => removeShare(share.id ?? null)}
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pendingShares.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    <Clock className="h-3.5 w-3.5 text-orange-600" />
                    {t('share.pending')} ({pendingShares.length})
                  </div>
                  <div className="space-y-1.5">
                    {pendingShares.map((share) => (
                      <div key={getShareKey(share)} className="flex items-center justify-between gap-2 p-2 rounded-lg border bg-muted/30">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="h-7 w-7 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                            <Clock className="h-3.5 w-3.5 text-orange-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium truncate">{share.user_email}</p>
                            <p className="text-xs text-muted-foreground">{t('share.waiting_accept')}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <Button
                            onClick={() => resendInvite(share.id ?? null)}
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs border-primary text-primary hover:bg-primary/10"
                          >
                            {t('resend')}
                          </Button>
                          <Button
                            onClick={() => removeShare(share.id ?? null)}
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {acceptedShares.length === 0 && pendingShares.length === 0 && !loading && (
                <div className="text-center py-6">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
                    <UserPlus className="h-5 w-5 text-primary" />
                  </div>
                  <p className="text-sm font-medium mb-1">
                    {t('share.no_collaborators')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {resourceType === "project" ? t('share.start_adding_project') : t('share.start_adding_meeting')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

